import { useMemo, useState } from 'react';
import { ChevronDown, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ChatTimelineEntry } from '@/lib/chat/types';

interface ToolTimelineProps {
  entries: ChatTimelineEntry[];
  durationMs?: number;
  isStreaming?: boolean;
  hasText?: boolean;
  className?: string;
}

interface TimelineStats {
  searches: number;
  recentLookups: number;
  reads: number;
  otherTools: number;
  steps: number;
  totalMatches: number;
  totalActions: number;
}

function formatDuration(durationMs?: number): string | null {
  if (!durationMs || durationMs < 0) return null;
  if (durationMs < 1000) return `${durationMs}ms`;
  return `${(durationMs / 1000).toFixed(1)}s`;
}

function asNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function asString(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

function truncate(value: string, maxLength = 60): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1)}…`;
}

function isInputState(state: string | null): boolean {
  return state === 'input-streaming' || state === 'input-available';
}

function formatToolType(type: string): string {
  const withSpaces = type
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .trim();

  if (!withSpaces) return 'tool';
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
}

function formatEntry(entry: ChatTimelineEntry): string {
  const state = asString(entry.state);

  if (entry.type === 'search') {
    const query = asString(entry.query);
    const queryLabel = query ? ` "${truncate(query, 40)}"` : '';
    const resultCount = asNumber(entry.resultCount);
    const resultLabel = resultCount !== null ? ` (${pluralize(resultCount, 'match')})` : '';

    if (state === 'output-error') return `Search failed${queryLabel}`;
    if (isInputState(state)) return `Searching${queryLabel}...`;
    return `Searched${queryLabel}${resultLabel}`;
  }

  if (entry.type === 'recent') {
    const resultCount = asNumber(entry.resultCount);
    const resultLabel = resultCount !== null ? ` (${pluralize(resultCount, 'result')})` : '';

    if (state === 'output-error') return 'Recent lookup failed';
    if (isInputState(state)) return 'Checking recent saves...';
    return `Checked recent saves${resultLabel}`;
  }

  if (entry.type === 'read') {
    const title = truncate(asString(entry.title) ?? 'webpage', 50);

    if (state === 'output-error') return `Failed to read ${title}`;
    if (isInputState(state)) return `Reading ${title}...`;
    return `Read ${title}`;
  }

  if (entry.type === 'step') {
    if (state === 'started') {
      return 'Reasoning...';
    }

    const calls = Array.isArray(entry.toolCalls) ? entry.toolCalls.length : 0;
    const results = Array.isArray(entry.toolResults) ? entry.toolResults.length : 0;

    if (calls > 0 || results > 0) {
      return `Step finished (${pluralize(calls, 'call')}, ${pluralize(results, 'result')})`;
    }
    return 'Reasoning step finished';
  }

  const typeLabel = typeof entry.type === 'string' ? formatToolType(entry.type) : 'Tool activity';
  if (state === 'output-error') return `${typeLabel} failed`;
  if (isInputState(state)) return `Running ${typeLabel.toLowerCase()}...`;
  return typeLabel;
}

function computeStats(entries: ChatTimelineEntry[]): TimelineStats {
  const stats: TimelineStats = {
    searches: 0,
    recentLookups: 0,
    reads: 0,
    otherTools: 0,
    steps: 0,
    totalMatches: 0,
    totalActions: 0,
  };

  for (const entry of entries) {
    if (entry.type === 'search') {
      stats.searches += 1;
      const resultCount = asNumber(entry.resultCount);
      if (resultCount !== null && resultCount >= 0) {
        stats.totalMatches += resultCount;
      }
      continue;
    }

    if (entry.type === 'recent') {
      stats.recentLookups += 1;
      const resultCount = asNumber(entry.resultCount);
      if (resultCount !== null && resultCount >= 0) {
        stats.totalMatches += resultCount;
      }
      continue;
    }

    if (entry.type === 'read') {
      stats.reads += 1;
      continue;
    }

    if (entry.type === 'step') {
      stats.steps += 1;
      continue;
    }

    if (typeof entry.type === 'string' && entry.type.length > 0) {
      stats.otherTools += 1;
    }
  }

  stats.totalActions = stats.searches + stats.recentLookups + stats.reads + stats.otherTools;
  return stats;
}

function formatActiveToolPhase(entry: ChatTimelineEntry): string {
  if (entry.type === 'search') {
    const query = asString(entry.query);
    return query ? `Searching "${truncate(query, 36)}"` : 'Searching your library';
  }

  if (entry.type === 'recent') {
    return 'Checking recent saves';
  }

  if (entry.type === 'read') {
    const title = asString(entry.title);
    return title ? `Reading ${truncate(title, 42)}` : 'Reading saved page';
  }

  const typeLabel = typeof entry.type === 'string' ? formatToolType(entry.type) : 'tool';
  return `Running ${typeLabel.toLowerCase()}`;
}

function activePhaseLabel(entries: ChatTimelineEntry[], hasText: boolean): string {
  const ordered = [...entries].reverse();
  const activeTool = ordered.find(entry => {
    if (entry.type === 'step') return false;
    return isInputState(asString(entry.state));
  });

  if (activeTool) {
    return formatActiveToolPhase(activeTool);
  }

  const lastEntry = ordered.find(entry => typeof entry.type === 'string' && entry.type.length > 0);
  if (!lastEntry) {
    return hasText ? 'Writing answer' : 'Thinking';
  }

  if (lastEntry.type === 'step') {
    return hasText ? 'Writing answer' : 'Thinking through results';
  }

  if (lastEntry.type === 'read') {
    return hasText ? 'Thinking through page details' : 'Reviewing page details';
  }

  if (lastEntry.type === 'search' || lastEntry.type === 'recent') {
    return hasText ? 'Thinking through search results' : 'Reviewing search results';
  }

  return hasText ? 'Writing answer' : 'Thinking through results';
}

function completedSummary(stats: TimelineStats, entriesLength: number): string {
  if (stats.totalActions <= 0) {
    if (stats.steps > 0) return `Completed ${pluralize(stats.steps, 'reasoning step')}`;
    return entriesLength > 0 ? `Completed ${pluralize(entriesLength, 'update')}` : 'Work details';
  }

  const parts = [`Completed ${pluralize(stats.totalActions, 'action')}`];
  const totalLookups = stats.searches + stats.recentLookups;
  if (totalLookups > 0) {
    parts.push(pluralize(totalLookups, 'search'));
  }
  if (stats.reads > 0) {
    parts.push(`${pluralize(stats.reads, 'page')} read`);
  }
  if (stats.totalMatches > 0) {
    parts.push(pluralize(stats.totalMatches, 'match'));
  }

  return parts.join(' · ');
}

export function ToolTimeline({
  entries,
  durationMs,
  isStreaming = false,
  hasText = false,
  className,
}: ToolTimelineProps) {
  const [expanded, setExpanded] = useState(false);

  const stats = useMemo(() => computeStats(entries), [entries]);

  const summary = useMemo(() => {
    if (isStreaming) {
      return `${activePhaseLabel(entries, hasText)}...`;
    }

    return completedSummary(stats, entries.length);
  }, [entries, hasText, isStreaming, stats]);

  if (!isStreaming && entries.length === 0) {
    return null;
  }

  return (
    <div className={cn('rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2', className)}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="-mx-1 h-auto w-full justify-between px-1 py-1 text-xs font-medium text-neutral-700"
        onClick={() => setExpanded(prev => !prev)}
      >
        <span className="inline-flex items-center gap-2">
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {isStreaming && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
          {summary}
        </span>
        <span className="text-[11px] text-neutral-500">{formatDuration(durationMs) ?? ''}</span>
      </Button>

      {expanded && (
        <ul className="mt-1 space-y-1 pl-5 text-xs text-neutral-600">
          {entries.length === 0 && isStreaming ? <li>Waiting for tool events...</li> : null}
          {entries.map((entry, index) => (
            <li key={`${entry.type}-${index}`}>{formatEntry(entry)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
